import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { Eye, EyeOff, Mail, Lock, User, MailCheck } from 'lucide-react';
import { useSiteSettings } from '../context/SiteSettingsContext';
import useSEO from '../hooks/useSEO';
import './AuthPage.css';

export default function RegisterPage() {
  const { settings } = useSiteSettings();
  const sn = settings.siteName;
  useSEO({ title: 'Daftar', siteName: sn, noindex: true });
  const { register, googleSignIn, user } = useAuth();
  const navigate = useNavigate();
  const googleBtnRef = useRef(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    // Jika sudah login (misal Google), arahkan ke portal
    if (user) navigate('/', { replace: true });
  }, [user]);

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId || !window.google) return;
    window.google.accounts.id.initialize({ client_id: clientId, callback: handleGoogleCallback });
    if (googleBtnRef.current) {
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: 'outline', size: 'large', shape: 'rectangular',
        width: '100%', text: 'signup_with', logo_alignment: 'center',
      });
    }
  }, []);

  const handleGoogleCallback = async (response) => {
    setLoading(true);
    try {
      const u = await googleSignIn(response.credential);
      toast.success(`Selamat datang, ${u.name}!`);
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Google Sign-In gagal');
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return toast.error('Password tidak cocok.');
    if (form.password.length < 6) return toast.error('Password minimal 6 karakter.');
    setLoading(true);
    try {
      await register(form.name, form.email, form.password);
      setRegistered(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registrasi gagal.');
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await API.post('/auth/resend-verification-public', { email: form.email });
      toast.success('Email verifikasi telah dikirim ulang.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal mengirim ulang.');
    } finally { setResending(false); }
  };

  return (
    <div className="auth-root">
      <div className="auth-topbar">
        <Link to="/" className="auth-logo" style={{ marginBottom: 0 }}>
          <img
            src={settings.logoAuthUrl || settings.logoUrl || "/logo-inspira.png"} alt={sn}
            style={{ height: 32, objectFit: 'contain' }}
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
          <span style={{ display: 'none', alignItems: 'center', gap: 6 }}>
            <span className="auth-logo-icon">IN</span>
            {sn}
          </span>
        </Link>
      </div>

      <div className="auth-body">
        <div className="auth-card">
          <Link to="/" className="auth-logo">
            <img
              src={settings.logoAuthUrl || settings.logoUrl || "/logo-inspira.png"} alt={sn}
              style={{ height: 36, objectFit: 'contain' }}
              onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
            />
            <span style={{ display: 'none', alignItems: 'center', gap: 6 }}>
              <span className="auth-logo-icon">IN</span>
              {sn}
            </span>
          </Link>

          {registered ? (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div style={{ fontSize: 52, marginBottom: 16 }}>📬</div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--in-blue)', marginBottom: 10 }}>
                Cek Email Anda!
              </h2>
              <p style={{ fontSize: 14, color: '#555', lineHeight: 1.75, marginBottom: 6 }}>
                Kami mengirim link verifikasi ke
              </p>
              <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--in-blue)', marginBottom: 20 }}>
                {form.email}
              </p>
              <p style={{ fontSize: 13, color: '#888', lineHeight: 1.7, marginBottom: 24 }}>
                Klik link di email tersebut untuk mengaktifkan akun Anda. Link berlaku 24 jam.<br />
                Cek folder <strong>Spam</strong> jika tidak ada di inbox.
              </p>
              <button
                onClick={handleResend} disabled={resending}
                style={{
                  background: 'none', border: '1px solid #ddd', borderRadius: 8,
                  padding: '9px 20px', fontSize: 13, color: '#555', cursor: 'pointer',
                  fontFamily: 'inherit', marginBottom: 16
                }}
              >
                {resending ? 'Mengirim...' : 'Kirim Ulang Email'}
              </button>
              <div>
                <Link to="/login" style={{ fontSize: 13, color: 'var(--in-blue)', fontWeight: 600 }}>
                  Sudah verifikasi? Masuk →
                </Link>
              </div>
            </div>
          ) : (
            <>
          <h1 className="auth-title">Buat Akun</h1>
          <p className="auth-sub">Bergabung dan mulai dokumentasikan karya Anda</p>

          {process.env.REACT_APP_GOOGLE_CLIENT_ID ? (
            <>
              <div ref={googleBtnRef} className="google-btn-wrap" />
              <div className="auth-divider"><span>atau daftar dengan email</span></div>
            </>
          ) : (
            <div className="auth-divider"><span>daftar dengan email</span></div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Nama Lengkap</label>
              <div className="input-icon-wrap">
                <User size={15} className="input-icon" />
                <input
                  type="text" className="form-input input-with-icon"
                  value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="Nama lengkap Anda" required
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <div className="input-icon-wrap">
                <Mail size={15} className="input-icon" />
                <input
                  type="email" className="form-input input-with-icon"
                  value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="email@domain.com" required
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Kata Sandi</label>
              <div className="input-icon-wrap">
                <Lock size={15} className="input-icon" />
                <input
                  type={showPw ? 'text' : 'password'}
                  className="form-input input-with-icon input-with-action"
                  value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                  placeholder="Minimal 6 karakter" required
                />
                <button type="button" className="input-action" onClick={() => setShowPw(!showPw)}>
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Konfirmasi Kata Sandi</label>
              <div className="input-icon-wrap">
                <Lock size={15} className="input-icon" />
                <input
                  type="password" className="form-input input-with-icon"
                  value={form.confirm} onChange={e => setForm({ ...form, confirm: e.target.value })}
                  placeholder="Ulangi kata sandi" required
                />
              </div>
            </div>
            <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={loading} style={{ marginTop: 4 }}>
              {loading ? 'Mendaftar...' : 'Daftar Sekarang'}
            </button>
          </form>

          <p className="auth-footer-text">
            Sudah punya akun? <Link to="/login" className="auth-link">Masuk</Link>
          </p>
          <p style={{ fontSize: 12, color: 'var(--in-text-faint)', textAlign: 'center', marginTop: 12 }}>
            Dengan mendaftar, Anda menyetujui ketentuan platform {sn}.
          </p>
            </>
          )}
        </div>
      </div>

      <div className="auth-footer">
        <p>© {new Date().getFullYear()} {sn} · {settings.copyrightText}</p>
        <div style={{ display: 'flex', gap: 16 }}>
          <a href="https://inspiratekno.com" target="_blank" rel="noopener noreferrer">inspiratekno.com</a>
          <a href="https://lms.inspiratekno.com" target="_blank" rel="noopener noreferrer">InspiraLearn</a>
        </div>
      </div>
    </div>
  );
}
